import { useSuspenseQuery } from '@tanstack/react-query';
import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { Alert, type AlertAction, type AlertProps } from '@gitbook/spine-react';

import { apiClient, apiQuery } from '../../state';
import { createDocumentationURL } from '../../utils';
import { DocumentationLink } from '..';

interface CustomHostnameStatusAlertProps {
    /** Custom hostname user selected. */
    hostname: string;

    /** CNAME of a hostname pointer. */
    cname: string;

    /** Enables overriding action the alerts have preset for some statuses. */
    actionOverride?: AlertAction;

    /** True by default, enables hiding default title for a status alert. */
    showTitle?: boolean;
}

/**
 * Get custom hostname infos.
 */
export function getCustomHostnameStatusInfos(
    customHostname: api.CustomHostname | null,
    cname: string
): {
    kind: AlertProps['kind'];
    title: string;
    details: AlertProps['details'];
    action?: AlertProps['action'];
} {
    switch (customHostname?.status) {
        case api.CustomHostnameErrorStatus.InternalError: {
            return {
                kind: 'danger',
                title: 'Oops',
                details:
                    'An error occurred while activating your domain. Contact GitBook support to get help.',
            };
        }
        case api.CustomHostnameErrorStatus.InvalidDomain: {
            return {
                kind: 'danger',
                title: 'Invalid hostname',
                details: 'This hostname is invalid. Only subdomains are allowed.',
            };
        }
        case api.CustomHostnameDnsStatus.DnsWrongCname: {
            return {
                kind: 'danger',
                title: 'Configuration missing',
                details: `The CNAME record for this hostname is not pointing to ${cname} or the update has not yet propagated.`,
            };
        }
        case api.CustomHostnameDnsStatus.DnsNoCname: {
            return {
                kind: 'danger',
                title: 'Configuration missing',
                details: `The provided hostname is missing a CNAME record pointing to ${cname} or the update has not yet propagated.`,
            };
        }
        case api.CustomHostnameDnsStatus.DnsWrongCaa: {
            return {
                kind: 'danger',
                title: 'Invalid configuration',
                details:
                    'A CAA record for this domain does not allow "pki.goog" to issue a certificate for this domain.',
            };
        }
        case api.CustomHostnameDnsStatus.DnsCloudflareProxied: {
            return {
                kind: 'info',
                title: 'Hostname proxied by Cloudflare',
                details: (
                    <>
                        This domain is proxied through Cloudflare, which is discouraged to ensure
                        the best experience for your public documentation.{' '}
                        <DocumentationLink path="published-documentation/custom-domain/configure-dns#are-you-using-cloudflare">
                            Learn more
                        </DocumentationLink>
                        .
                    </>
                ),
            };
        }
        case api.CustomHostnameDnsStatus.DnsPassed:
        case api.CustomHostnameSslStatus.SslUnknown:
        case api.CustomHostnameSslStatus.SslPending: {
            return {
                kind: 'info',
                title: 'Provisioning SSL',
                details:
                    'DNS for your domain is correctly configured, we are preparing the SSL certificate. Your domain will be live in a minute!',
            };
        }
        case api.CustomHostnameSslStatus.SslFailed: {
            return {
                kind: 'danger',
                title: 'SSL Error',
                details: 'An error occurred while provisioning your SSL certificate.',
                action: {
                    label: 'More info',
                    href: createDocumentationURL(
                        'publishing/custom-domain/troubleshooting#ssl-error-an-error-occurred-when-provisioning-your-ssl-certificate.'
                    ),
                    target: '_blank',
                },
            };
        }
        case api.CustomHostnameSslStatus.SslRetryExpired: {
            return {
                kind: 'danger',
                title: 'SSL Error',
                details:
                    "We couldn't determine your SSL certificate status yet. This could take a few minutes. Please try again.",
                action: {
                    label: 'Retry',
                    onPress: async () => {
                        await apiClient.customHostnames.dnsRevalidateCustomHostname(
                            customHostname.hostname
                        );
                    },
                },
            };
        }
        case api.CustomHostnameSslStatus.Live: {
            return {
                kind: 'success',
                title: 'Domain connected',
                details: `Your domain ${customHostname.hostname} is properly configured and now active.`,
            };
        }
        default: {
            return {
                kind: 'info',
                title: 'In progress',
                details:
                    'This may take a few minutes, feel free to close this window and we’ll notify you when you’re all set up and connected.',
            };
        }
    }
}

/**
 * Alert for a status of a hostname.
 */
export function CustomHostnameStatusAlert(
    props: CustomHostnameStatusAlertProps
): React.ReactElement | null {
    const { hostname, cname, actionOverride, showTitle = true } = props;
    const { data: customHostname } = useSuspenseQuery(
        apiQuery('getCustomHostname', { hostname }, { nullOnError: true })
    );
    const infos = getCustomHostnameStatusInfos(customHostname, cname);

    return (
        <Alert
            kind={infos.kind}
            title={showTitle ? infos.title : undefined}
            details={infos.details}
            action={actionOverride ?? infos.action}
        />
    );
}
